import { StoreApi, UseBoundStore } from 'zustand'

export const isKeyedObject = (obj: unknown): obj is Record<string, unknown> =>
  !!obj &&
  typeof obj === 'object' &&
  !Array.isArray(obj) &&
  typeof obj !== 'function'

const mergeStatePreservingFunctions = (
  nextState: unknown,
  previousState: unknown,
): unknown => {
  if (typeof nextState === 'function') {
    return nextState
  }

  if (Array.isArray(nextState)) {
    return Array.isArray(previousState) ? previousState : nextState
  }

  if (isKeyedObject(nextState) && isKeyedObject(previousState)) {
    const mergedState: Record<string, unknown> = {}

    Object.keys(nextState).forEach(key => {
      const nextValue = (nextState as Record<string, unknown>)[key]
      const previousValue = (previousState as Record<string, unknown>)[key]

      mergedState[key] = mergeStatePreservingFunctions(nextValue, previousValue)
    })

    return mergedState
  }

  return previousState === undefined ? nextState : previousState
}

export const updateOrAppend = <T>(list: T[], item: T, idProperty: keyof T) => {
  const existingItem = list.find(i => i[idProperty] === item[idProperty])

  if (!existingItem) {
    return [...list, item]
  }

  return list.map(i => (i[idProperty] === item[idProperty] ? item : i))
}

/// <reference types="vite/client" />

export const zustandHmrFix = <T extends Record<string, unknown>>(
  name: string,
  useStore: UseBoundStore<StoreApi<T>>,
) => {
  if (import.meta.hot?.data) {
    const previousState = import.meta.hot.data[name]
    if (previousState) {
      const mergedState = mergeStatePreservingFunctions(
        useStore.getState(),
        previousState,
      )
      useStore.setState(mergedState as T, true)
    }

    const unsubscribe = useStore.subscribe(state => {
      import.meta.hot!.data[name] = state
    })

    import.meta.hot.accept()
    import.meta.hot.dispose(() => {
      unsubscribe()
      import.meta.hot!.data[name] = useStore.getState()
    })
  }
}
